import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { supabase } from '../lib/supabase'

// ── OpsAlertBanner ────────────────────────────────────────────────────────────
// The founder "War Room" — a deliberately loud, admin-only bar pinned to the very
// top of the app for high-priority ops alerts (bug reports, signup-cap milestones).
// Separate from the user activity/notification bell so a real launch signal never
// gets lost in normal noise. Reads is_admin()-gated admin_ops_alerts(); tapping ✕
// acknowledges an alert (admin_ack_ops_alert) and drops it. Renders nothing for
// non-admins and nothing when there's nothing to see.

export function OpsAlertBanner({ profile }) {
  const navigate = useNavigate()
  const isAdmin = !!profile?.is_admin
  const [alerts, setAlerts] = useState([])
  const [leavingIds, setLeavingIds] = useState(new Set())

  const load = useCallback(async () => {
    if (!isAdmin) return
    try {
      const { data, error } = await supabase.rpc('admin_ops_alerts')
      if (!error && Array.isArray(data)) setAlerts(data)
    } catch { /* transient, keep what we have */ }
  }, [isAdmin])

  useEffect(() => {
    if (!isAdmin) return
    load()
    const t = setInterval(load, 60000)   // poll every 60s; these are rare + high-value
    return () => clearInterval(t)
  }, [isAdmin, load])

  const ack = async (id) => {
    // Slide/fade the row out first (opsRowLeave), THEN drop it from state — an
    // instant filter() just made the acknowledged row vanish (Jason 2026-08-08:
    // small compounding polish pass).
    setLeavingIds(prev => new Set(prev).add(id))
    setTimeout(() => {
      setAlerts(a => a.filter(x => x.id !== id))
      setLeavingIds(prev => { const n = new Set(prev); n.delete(id); return n })
    }, 220)
    try { await supabase.rpc('admin_ack_ops_alert', { p_id: id }) } catch { load() }
  }

  if (!isAdmin || !alerts.length) return null

  const critical = alerts.some(a => a.severity === 'critical')

  return (
    <div style={{
      position:'fixed', top:0, left:0, right:0, zIndex:'var(--z-max)',
      background: critical
        ? 'linear-gradient(180deg, #7f1d1d, #611515)'
        : 'linear-gradient(180deg, #7a5a12, #5e440d)',
      borderBottom:`1px solid ${critical ? 'rgba(255,120,120,0.5)' : 'rgba(255,215,106,0.5)'}`,
      boxShadow:'0 6px 24px rgba(0,0,0,0.5)',
      paddingTop:'env(safe-area-inset-top)',
      fontFamily:'var(--font)', '--nxa':'opsBannerIn 0.3s ease',
    }}>
      <style>{`@keyframes opsBannerIn{from{transform:translateY(-100%)}to{transform:translateY(0)}}
        @keyframes opsPulse{0%,100%{opacity:1}50%{opacity:0.45}}
        @keyframes opsRowLeave{from{opacity:1;max-height:60px}to{opacity:0;max-height:0;padding-top:0;padding-bottom:0}}`}</style>
      {alerts.slice(0, 4).map(a => (
        <div key={a.id} style={{
          display:'flex', alignItems:'center', gap:10, padding:'9px 14px',
          borderTop:'1px solid rgba(var(--ink-rgb),0.08)', overflow:'hidden',
          ...(leavingIds.has(a.id) ? { '--nxa':'opsRowLeave 220ms ease-out forwards' } : null),
        }}>
          <AlertTriangle size={17} color={a.severity === 'critical' ? '#ff9a9a' : '#ffe08a'}
            style={{ flexShrink:0, '--nxa':'opsPulse 1.6s ease-in-out infinite' }}/>
          <div onClick={() => navigate('/admin?tab=warroom')} style={{ flex:1, minWidth:0, cursor:'pointer' }}>
            <div style={{ color:'#fff', fontWeight:700, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.title}</div>
            {a.body && <div style={{ color:'rgba(var(--ink-rgb),0.75)', fontSize:11.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.body}</div>}
          </div>
          <button onClick={() => ack(a.id)} aria-label="Acknowledge" style={{
            flexShrink:0, width:38, height:38, borderRadius:'50%', border:'1px solid rgba(var(--ink-rgb),0.25)',
            background:'rgba(var(--ink-rgb),0.1)', color:'#fff', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <X size={14}/>
          </button>
        </div>
      ))}
      {alerts.length > 4 && (
        <div style={{ padding:'5px 14px', color:'rgba(var(--ink-rgb),0.7)', fontSize:11, textAlign:'center', borderTop:'1px solid rgba(var(--ink-rgb),0.08)' }}>
          +{alerts.length - 4} more · open Admin → War Room
        </div>
      )}
    </div>
  )
}
